import { useState } from 'react';
import {
  Color, Size, formatDateOnly, t, timeOffEnrollmentEndPath, timeOffEnrollmentEndProblem, timeOffEnrollmentProblems, timeOffEnrollmentStart, timeOffEnrollmentsPath,
  type DateFormat, type TimeOffEnrollmentDraft, type TimeOffEnrollmentRow, type TimeOffEnrollmentSection,
} from '@bms2/shared';
import { Button, DatePicker, NumericInput, Select, TextInput } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useDateFormat } from '../../shell/ViewerZone';
import { post } from '../records/api';
import styles from './HrPages.module.css';

/**
 * Enroll in a Time Off Plan (piece 34, *PTO: plans and enrollment*; decision
 * 13130): the plan, the day the job joins it, the opening balance in hours and
 * a reason. 🔒 Drawn through the Form frame, the shape of Edit Working Pattern;
 * the save writes the enrollment and its opening ledger row together, and the
 * refusal the route answers shows in the frame.
 */
export function EnrollTimeOffWindow({ apiBaseUrl = '', employmentId, section, onClose, onSaved }: {
  apiBaseUrl?: string;
  employmentId: string;
  section: TimeOffEnrollmentSection;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<TimeOffEnrollmentDraft>(() => timeOffEnrollmentStart(section));
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (next: Partial<TimeOffEnrollmentDraft>) => {
    setTyped(true);
    setDraft((was) => ({ ...was, ...next }));
  };
  const problems = timeOffEnrollmentProblems(draft, { startDate: section.jobStartDate, endDate: section.jobEndDate });
  const first = Object.values(problems)[0] ?? '';
  const save = async () => {
    const answer = await post<{ enrollmentId: string }>(timeOffEnrollmentsPath(employmentId), { ...draft, reason: draft.reason.trim() }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.timeOffPlans.enrollTitle')}
      dataForm="enroll-time-off"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.timeOffPlans.enrollAct'), onAct: () => save(), disabled: first !== '', disabledReason: first }}
    >
      <div className={styles.fields} data-enroll-time-off={employmentId}>
        <p className={styles.meta}>{t('hr.timeOffPlans.enrollLead')}</p>
        <Select
          size={Size.Medium}
          label={t('hr.timeOffPlans.plan')}
          value={draft.planId}
          onChange={(planId) => change({ planId })}
          options={section.plans.map((plan) => ({ value: plan.id, label: plan.name }))}
          placeholder={t('hr.timeOffPlans.choosePlan')}
          error={typed ? (problems.planId ?? null) : null}
          dataAttribute={{ 'data-enroll-time-off-plan': 'true' }}
          required
        />
        <DatePicker
          size={Size.Medium}
          label={t('hr.timeOffPlans.startDate')}
          value={draft.startDate}
          min={section.jobStartDate}
          {...(section.jobEndDate ? { max: section.jobEndDate } : {})}
          onChange={(startDate) => change({ startDate })}
          error={typed ? (problems.startDate ?? null) : null}
          required
          name="enrollTimeOffStart"
        />
        <NumericInput
          size={Size.Small}
          kind="number"
          decimals={2}
          label={t('hr.timeOffPlans.openingHours')}
          value={draft.openingHours}
          onChange={(openingHours) => change({ openingHours })}
          hint={t('hr.timeOffPlans.openingHint')}
          error={typed ? (problems.openingHours ?? null) : null}
          required
          name="enrollTimeOffHours"
        />
        <TextInput
          size={Size.Large}
          label={t('hr.timeOffPlans.reason')}
          value={draft.reason}
          onChange={(reason) => change({ reason })}
          hint={t('hr.timeOffPlans.reasonHint')}
          error={typed ? (problems.reason ?? null) : null}
          required
          name="enrollTimeOffReason"
        />
      </div>
    </FormFrame>
  );
}

/** End This Plan (decision 13130): the last day the plan applies, stamped once; the balance stays on the ledger. */
export function EndTimeOffWindow({ apiBaseUrl = '', employmentId, enrollment, today, onClose, onSaved }: {
  apiBaseUrl?: string;
  employmentId: string;
  enrollment: TimeOffEnrollmentRow;
  today: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [endDate, setEndDate] = useState(() => (enrollment.startDate > today ? enrollment.startDate : today));
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const problem = timeOffEnrollmentEndProblem({ endDate }, enrollment) ?? '';
  const save = async () => {
    const answer = await post<{ enrollmentId: string }>(timeOffEnrollmentEndPath(employmentId, enrollment.id), { endDate }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.timeOffPlans.endTitle')}
      dataForm="end-time-off"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.timeOffPlans.endAct'), onAct: () => save(), disabled: problem !== '', disabledReason: problem }}
    >
      <div className={styles.fields} data-end-time-off={enrollment.id}>
        <p className={styles.meta}>{enrollment.planName}</p>
        <p className={styles.meta}>{t('hr.timeOffPlans.endLead')}</p>
        <DatePicker
          size={Size.Medium}
          label={t('hr.timeOffPlans.endDate')}
          value={endDate}
          min={enrollment.startDate}
          onChange={(next) => {
            setTyped(true);
            setEndDate(next);
          }}
          error={typed && problem !== '' ? problem : null}
          required
          name="endTimeOffDate"
        />
      </div>
    </FormFrame>
  );
}

/** A running enrollment beside its End: the plan and the day it began, in the reader's own date format. */
function enrollmentLabel(row: TimeOffEnrollmentRow, format: DateFormat): string {
  return [row.planName, formatDateOnly(`${row.startDate}T00:00:00.000Z`, format)].join(' · ');
}

/**
 * The Job section's time-off acts (decision 13130): Enroll in a Plan, and End
 * on each enrollment still running. 🔒 The file sends the section to HR reading
 * as HR with the hire permission alone, so the buttons draw where the routes
 * answer; Enroll is drawn disabled, saying why, where nothing is left to join.
 */
export function TimeOffEnrollmentControls({ apiBaseUrl, employmentId, section, onSaved }: { apiBaseUrl: string; employmentId: string; section: TimeOffEnrollmentSection; onSaved: () => void }) {
  const format = useDateFormat();
  const [enrolling, setEnrolling] = useState(false);
  const [ending, setEnding] = useState<TimeOffEnrollmentRow | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // 🔨 A reply missing a list reads as an empty one, so the section never breaks on a short answer.
  const plans = section.plans ?? [];
  const running = (section.enrollments ?? []).filter((row) => row.endDate === null);
  const why = section.ended ? t('hr.timeOffPlans.endedJob') : plans.length === 0 ? t('hr.timeOffPlans.everyPlan') : null;
  const saved = (line: string) => {
    setEnrolling(false);
    setEnding(null);
    setSaid(line);
    onSaved();
  };
  return (
    <div className={styles.fields} data-employee-time-off-plans={employmentId}>
      <div className={styles.controls}>
        <span data-time-off-enroll="open">
          <Button size={Size.Small} color={Color.Navy} text={[t('hr.timeOffPlans.enroll')]} disabled={why !== null} {...(why ? { disabledReason: why } : {})} actions={[() => { setSaid(null); setEnrolling(true); }]} />
        </span>
        {why ? <span className={styles.meta} data-time-off-enroll-why="true">{why}</span> : null}
      </div>
      {running.map((row) => (
        <div key={row.id} className={styles.controls} data-time-off-enrollment={row.id}>
          <span className={styles.meta}>{enrollmentLabel(row, format)}</span>
          <span data-time-off-end={row.id}><Button size={Size.Small} color={Color.Inactive} text={[t('hr.timeOffPlans.end')]} actions={[() => { setSaid(null); setEnding(row); }]} /></span>
        </div>
      ))}
      {said ? <p className={styles.meta} role="status" data-time-off-plans-saved="true">{said}</p> : null}
      {/* 🔨 Each window is mounted only while open and keyed to what it writes, so a reopening reads the section afresh. */}
      {enrolling ? (
        <EnrollTimeOffWindow key={`enroll-${employmentId}`} apiBaseUrl={apiBaseUrl} employmentId={employmentId} section={{ ...section, plans }} onClose={() => setEnrolling(false)} onSaved={() => saved(t('hr.timeOffPlans.enrolled'))} />
      ) : null}
      {ending ? (
        <EndTimeOffWindow key={`end-${ending.id}`} apiBaseUrl={apiBaseUrl} employmentId={employmentId} enrollment={ending} today={section.today} onClose={() => setEnding(null)} onSaved={() => saved(t('hr.timeOffPlans.endedSaved'))} />
      ) : null}
    </div>
  );
}
